import {
  FetchHttpRequest,
  FetchRequestOptions,
  HttpException,
  DefaultHttpExceptionType,
} from "http-repository-cache";
import { useAuthStore } from "core/stores/authStore";
import { ApiResponse } from "../api/types/ApiResponse";
import ViteEnv from "details/env/vite/ViteEnv";
import EnvKeys from "core/env/EnvKeys";
import i18n from "i18next";
import StringsKeys from "core/internationalization/strings/StringsKeys";
import browserPaths from "details/navigation/browser/browserPaths";
import ToastService from "app/shared/services/toastService";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** Type d'exception HTTP d'un statut d'échec, comme la bibliothèque le pose pour les requêtes JSON. */
function httpExceptionTypeOf(status: number): DefaultHttpExceptionType {
  switch (status) {
    case 400:
      return DefaultHttpExceptionType.BAD_REQUEST;
    case 401:
      return DefaultHttpExceptionType.UNAUTHORIZED;
    case 402:
      return DefaultHttpExceptionType.PAYMENT_REQUIRED;
    case 403:
      return DefaultHttpExceptionType.FORBIDDEN;
    case 404:
      return DefaultHttpExceptionType.NOT_FOUND;
    case 409:
      return DefaultHttpExceptionType.CONFLICT;
    case 503:
      return DefaultHttpExceptionType.SERVER_UNAVAILABLE;
    default:
      return DefaultHttpExceptionType.UNKNOWN_ERROR;
  }
}

export interface RequestConfig<TBody> extends Omit<
  FetchRequestOptions,
  "url" | "body" | "headers"
> {
  url: string;
  body?: TBody;
  withAuth?: boolean;
  headers?: Record<string, string>;
}

export interface HttpClient {
  get<TResponse>(config: RequestConfig<undefined>): Promise<TResponse>;
  post<TResponse, TBody = unknown>(
    config: RequestConfig<TBody>,
  ): Promise<TResponse>;
  put<TResponse, TBody = unknown>(
    config: RequestConfig<TBody>,
  ): Promise<TResponse>;
  patch<TResponse, TBody = unknown>(
    config: RequestConfig<TBody>,
  ): Promise<TResponse>;
  delete<TResponse, TBody = unknown>(config: RequestConfig<TBody>): Promise<TResponse>;
}

export class DefaultHttpClient implements HttpClient {
  constructor(
    private readonly httpRequest: FetchHttpRequest<FetchRequestOptions>,
    private readonly onUnauthorized?: () => void,
  ) {}

  private get baseUrl(): string | undefined {
    const env = new ViteEnv();
    const backendBaseUrl = env.get(EnvKeys.BACKEND_BASE_URL);
    return backendBaseUrl;
  }

  private get defaultOptions(): Partial<FetchRequestOptions> | undefined {
    const env = new ViteEnv();
    const timeoutMs = Number(env.get(EnvKeys.TIMEOUT_MS) || 15000);
    return timeoutMs
      ? ({ timeout: timeoutMs } as unknown as Partial<FetchRequestOptions>)
      : undefined;
  }

  async get<TResponse>(config: RequestConfig<undefined>): Promise<TResponse> {
    return this.execute<TResponse, undefined>("GET", {
      ...config,
      body: undefined,
    });
  }

  async post<TResponse, TBody = unknown>(
    config: RequestConfig<TBody>,
  ): Promise<TResponse> {
    return this.execute<TResponse, TBody>("POST", config);
  }

  async put<TResponse, TBody = unknown>(
    config: RequestConfig<TBody>,
  ): Promise<TResponse> {
    return this.execute<TResponse, TBody>("PUT", config);
  }

  async patch<TResponse, TBody = unknown>(
    config: RequestConfig<TBody>,
  ): Promise<TResponse> {
    return this.execute<TResponse, TBody>("PATCH", config);
  }

  async delete<TResponse, TBody = unknown>(
    config: RequestConfig<TBody>,
  ): Promise<TResponse> {
    return this.execute<TResponse, TBody>("DELETE", config);
  }

  private async execute<TResponse, TBody = unknown>(
    method: HttpMethod,
    config: RequestConfig<TBody>,
  ): Promise<TResponse> {
    const { url, body, withAuth, headers, ...rest } = config;
    const finalUrl = this.baseUrl
      ? `${this.baseUrl.replace(/\/$/, "")}/${url.replace(/^\//, "")}`
      : url;

    const authToken = withAuth
      ? useAuthStore.getState().user?.token
      : undefined;

    // If sending FormData, let the browser set the multipart boundary
    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;

    // Build headers: do NOT set Content-Type for FormData
    const finalHeaders: Record<string, string> = {
      ...(headers || {}),
    };
    if (!isFormData) {
      finalHeaders["Content-Type"] = "application/json";
    }

    if (authToken) {
      finalHeaders["X-App-Token"] = `Bearer ${authToken}`;
    }

    // Store the request URL for 401 detection
    const requestContext = { url: finalUrl, method };

    try {
      const mergedOptions = {
        ...(this.defaultOptions || {}),
        ...rest,
      } as FetchRequestOptions;

      if (isFormData) {
        const timeout = (this.defaultOptions as { timeout?: number })?.timeout;
        const signal = timeout ? AbortSignal.timeout(timeout) : undefined;
        const raw = await fetch(finalUrl, {
          method,
          headers: finalHeaders,
          body: body as unknown as BodyInit,
          ...(signal ? { signal } : {}),
        });
        if (!raw.ok) {
          // Un envoi refusé (droit, taille, quota, indisponibilité) doit échouer comme n'importe quelle requête : lu comme une réponse
          // ordinaire, son corps d'erreur passait pour un succès sans donnée et la suite du parcours repartait sans la pièce.
          throw Object.assign(new Error(`HTTP ${raw.status}`), {
            type: httpExceptionTypeOf(raw.status),
            status: raw.status,
            body: await raw.json().catch(() => null),
          });
        }
        return (await raw.json()) as TResponse;
      }

      let response: TResponse;

      switch (method) {
        case "GET":
          response = await this.httpRequest.get<TResponse>({
            url: finalUrl,
            headers: finalHeaders,
            ...mergedOptions,
          });
          break;
        case "POST":
          response = await this.httpRequest.post<TResponse, TBody>({
            url: finalUrl,
            body,
            headers: finalHeaders,
            ...mergedOptions,
          });
          break;
        case "PUT":
          response = await this.httpRequest.put<TResponse, TBody>({
            url: finalUrl,
            body,
            headers: finalHeaders,
            ...mergedOptions,
          });
          break;
        case "PATCH":
          response = await this.httpRequest.patch<TResponse, TBody>({
            url: finalUrl,
            body,
            headers: finalHeaders,
            ...mergedOptions,
          });
          break;
        case "DELETE":
          response = await this.httpRequest.delete<TResponse>({
            url: finalUrl,
            headers: finalHeaders,
            ...mergedOptions,
          });
          break;
      }

      return response;
    } catch (error) {
      // Unified 401 handling hook
      const httpError = error as
        | ApiResponse<unknown>
        | HttpException<unknown, unknown>
        | unknown;
      console.log("📄 DefaultHttpClient: Error", httpError);

      // Handle CONFLICT error type with API error format in body
      const errorWithType = httpError as Record<string, unknown>;
      if (errorWithType?.type === "CONFLICT" && errorWithType?.body) {
        const body = errorWithType.body as Record<string, unknown>;
        if (
          body &&
          typeof body === "object" &&
          "code" in body &&
          "message" in body
        ) {
          // Create a structured error that can be handled by ApiResponseHandler
          const apiError = {
            code: body.code,
            message: body.message,
            data: body.data || null,
            type: "CONFLICT",
          };
          throw apiError;
        }
      }

      // Check for 401 Unauthorized using both status code and type
      const status = (httpError as Record<string, unknown>)?.status as
        | number
        | undefined;
      const errorType = (httpError as HttpException<unknown, unknown>)?.type;

      // Check if error body is a JSON parse error, which might indicate a 401 with empty body
      const errorBody = errorWithType?.body;
      const isJsonParseError =
        errorBody instanceof Error && errorBody.message?.includes("json");

      console.log(
        "📄 DefaultHttpClient: Checking 401 - status:",
        status,
        "type:",
        errorType,
        "isJsonParseError:",
        isJsonParseError,
        "url:",
        requestContext.url,
      );

      // For JSON parse errors (which happen on 401 with empty body),
      // make a fetch call to check the actual status
      let actualStatus = status;
      if (isJsonParseError && errorType === "UNKNOWN_ERROR" && !status) {
        try {
          const testResponse = await fetch(requestContext.url, {
            method: requestContext.method,
            headers: finalHeaders,
          });
          actualStatus = testResponse.status;
          console.log(
            "📄 DefaultHttpClient: Actual response status:",
            actualStatus,
          );
        } catch {
          // Ignore fetch errors
        }
      }

      if (
        (actualStatus === 401 ||
          status === 401 ||
          errorType === DefaultHttpExceptionType.UNAUTHORIZED) &&
        this.onUnauthorized
      ) {
        console.log(
          "📄 DefaultHttpClient: 401 detected, triggering onUnauthorized callback",
        );
        try {
          this.onUnauthorized();
        } catch (error) {
          console.error(
            "📄 DefaultHttpClient: Error in onUnauthorized callback:",
            error,
          );
        }
      }

      // Re-throw to be handled by repository-level handler
      throw httpError;
    }
  }
}

// Flag to prevent multiple simultaneous 401 handlers
let isHandling401 = false;

export function createDefaultHttpClient(
  httpRequest: FetchHttpRequest<FetchRequestOptions>,
): HttpClient {
  return new DefaultHttpClient(httpRequest, () => {
    // Prevent multiple simultaneous 401 handlers
    if (isHandling401) {
      return;
    }
    isHandling401 = true;

    try {
      // Get translated messages using i18next
      const sessionExpiredTitle = i18n.t(StringsKeys.sessionExpired);
      const sessionExpiredMessage = i18n.t(StringsKeys.sessionExpiredMessage);

      // Show toast notification to user via centralized ToastService
      ToastService.error(sessionExpiredTitle, {
        description: sessionExpiredMessage,
        duration: 6000,
      });

      // Clear authentication state
      useAuthStore.getState().logout();

      // Redirect to login page after a short delay to ensure state is cleared
      setTimeout(() => {
        window.location.hash = `#${browserPaths.HOME}`;
        isHandling401 = false;
      }, 500);
    } catch (error) {
      console.error("🔐 401 Handler: Error handling 401 unauthorized:", error);
      // Reset flag even on error
      isHandling401 = false;
    }
  });
}
